/**
 * The relay-style pagination field policies for the Apollo cache.
 *
 * Extracted from the provider verbatim when the SE-6975 additions pushed it
 * over the file-size cap — one cohesive map, no behavior change. Each entry's
 * key args answer one question: which variables make a DIFFERENT list, rather
 * than another page of the same one.
 * @module providers/apolloPaginationPolicies
 */

import { relayStylePagination } from "@apollo/client/utilities";

import type { FieldPolicy } from "@apollo/client/cache";

/** Query fields whose connections append across pages. */
export const PAGINATED_QUERY_FIELDS: Record<string, FieldPolicy> = {
  listMyOrganizationsPlayers: relayStylePagination([
    "sortBy",
    "sortOrder",
    "positionIds",
    "sortByCustomStatName",
  ]),
  listMyOrganizationsTeams: relayStylePagination(),
  listMyOrganizationsEligibleTeams: relayStylePagination(["search"]),
  listLeagues: relayStylePagination(["search"]),
  listOrganizations: relayStylePagination(),
  listMyOrganizationsSentInvitations: relayStylePagination(["status"]),
  listMySentInvitations: relayStylePagination(),
  listMyReceivedInvitations: relayStylePagination(),
  listMyOrganizationsOrganizationUsers: relayStylePagination(),
  searchPlayers: relayStylePagination(["query", "sortBy", "sortOrder"]),
  filterPlayers: relayStylePagination([
    "filter",
    "sortBy",
    "sortOrder",
    "sortByCustomStatName",
  ]),
  listSimilarPlayers: relayStylePagination(["playerId", "filter"]),
  listNotesByPlayerId: relayStylePagination(["playerId"]),
  listPlayerMatches: relayStylePagination(["playerId"]),
  // SE-6984/SE-6986/SE-6987: match-report lists (including a player's
  // report history) page to completion; a different filter is a different list.
  listMatchReports: relayStylePagination(["filter"]),
  // SE-6975: paged to completion; same filter appends, a
  // different window or timeframe must not merge into it.
  listMatches: relayStylePagination(["filter"]),
  listAssignments: relayStylePagination(["filter"]),
  listWatchlistPlayers: relayStylePagination([
    "watchlistId",
    // SE-7136: the kanban Add Players view batches with the
    // plural arg; without it in the key, every selection
    // collapses onto one shared cache bucket and result sets
    // from different watchlist selections overwrite each other.
    "watchlistIds",
    "sortBy",
    "sortOrder",
    // SE-6496: server-side GPV bounds change the result set, so
    // filtered and unfiltered pages must not merge.
    "minGeminiPlayerValuation",
    "maxGeminiPlayerValuation",
  ]),
  listAllScoutingTargets: relayStylePagination([
    "search",
    "filterByPositionIds",
    "filterByTeamIds",
    "filterByWatchlistIds",
    "filterByScoutIds",
    "filterByNationalityIds",
    "filterByLeagueIds",
    "filterByRoleArchetypes",
    "sortBy",
    "sortOrder",
    "sortByCustomStatName",
    "minAge",
    "maxAge",
    "minGeminiPlayerValuation",
    "maxGeminiPlayerValuation",
    "minContractMonths",
    "maxContractMonths",
    "minGpr",
    "maxGpr",
    "minFitScore",
    "maxFitScore",
    "minTeamStyleFit",
    "maxTeamStyleFit",
    "minCarrying",
    "maxCarrying",
    "minPassing",
    "maxPassing",
    "minDribbling",
    "maxDribbling",
    "minShooting",
    "maxShooting",
    "minGkShotStopping",
    "maxGkShotStopping",
    "minGkPositioning",
    "maxGkPositioning",
    "minGkPassing",
    "maxGkPassing",
  ]),
  organizationScoutReports: relayStylePagination(["filter", "sort"]),
  listKanbanCards: relayStylePagination(["kanbanBoardId"]),
  listShadowTeamPlayers: relayStylePagination(["query", "shadowTeamId"]),
  searchAvailablePlayersForShadowTeam: relayStylePagination([
    "shadowTeamId",
    "query",
  ]),
};
